// tempexp_18907-full-file
import { Icon20AlarmOutline, Icon20Cancel } from '@vkontakte/icons';
import { classNames, IconButton } from '@vkontakte/vkui';
import React, { memo, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { discountInOutflowValue } from 'reactApp/appHelpers/featuresHelpers/features/discountInOutflow';
import type { BillingDataSubscriptionInfo } from 'reactApp/modules/billingData/billingData.types';
import { storeHelper } from 'reactApp/modules/promo/promo.helpers';
import { isCurrentDarkTheme } from 'reactApp/modules/theme/theme.selectors';
import {
    clearDiscountInOtflow,
    DISCOUNT_IN_OUTFLOW_TIMER,
    discountInOutflowLogger,
    RADAR_NAME,
} from 'reactApp/ui/DiscountInOutflow/DiscountInOutflow.const';
import { DiscountInOutflowSaveModalType } from 'reactApp/ui/DiscountInOutflow/DiscountInOutflowSaveModal/DiscountInOutflowSaveModal';
import {
    closeDiscountInOutflowSaveModal,
    renderDiscountInOutflowSaveModal,
} from 'reactApp/ui/DiscountInOutflow/DiscountInOutflowSaveModal/DiscountInOutflowSaveModal.helpers';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';
import { daysPluralNominative } from 'reactApp/utils/pluralHelpers';
import { getTimeToDate, secondsToDays } from 'reactApp/utils/timeHelpers';

import styles from './DiscountTimer.css';
import { Timer } from './Timer';

interface IProps {
    subscription: BillingDataSubscriptionInfo;
    expirationDate: Date;
    setVisible: (visibility: boolean) => void;
}

const useCallbacks = ({ setVisible, subscription, remainingDays }) => {
    const handleClick = useCallback(() => {
        sendPaymentGa({
            action: 'click',
            eventCategory: ECategoryGa.banner,
            name: RADAR_NAME,
        });
        emitAnalyticEvent(AnalyticEventNames.DISCOUNT_TIMER_CLICK);

        renderDiscountInOutflowSaveModal({
            analyticType: DiscountInOutflowSaveModalType.base,
            subscription,
            availabilityPeriod: remainingDays,
        });
    }, [remainingDays, subscription]);

    const handleCancel = useCallback(() => {
        setVisible(false);
        storeHelper.setValue(DISCOUNT_IN_OUTFLOW_TIMER, { closed: Date.now() });
        sendPaymentGa({
            action: 'cancel',
            eventCategory: ECategoryGa.banner,
            name: RADAR_NAME,
        });
        emitAnalyticEvent(AnalyticEventNames.DISCOUNT_TIMER_CANCEL);
    }, [setVisible]);

    return { handleClick, handleCancel };
};

export const DiscountTimer = memo<IProps>(({ expirationDate, setVisible, subscription }) => {
    discountInOutflowLogger.info('block render', expirationDate);

    const isDarkTheme = useSelector(isCurrentDarkTheme);

    const calculateTimer = useCallback(() => {
        const remainingSeconds = getTimeToDate(expirationDate) / 1000;
        const remainingDaysExact = secondsToDays(remainingSeconds);
        const remainingDays = remainingDaysExact < 1 ? 0 : Math.ceil(remainingDaysExact);
        discountInOutflowLogger.info('recalculate date', { remainingSeconds });
        return { remainingSeconds, remainingDays };
    }, [expirationDate]);

    const { remainingDays } = calculateTimer();

    useEffect(() => {
        sendPaymentGa({
            action: 'show',
            eventCategory: ECategoryGa.banner,
            name: RADAR_NAME,
        });
        emitAnalyticEvent(AnalyticEventNames.DISCOUNT_TIMER_SHOW);
    }, []);

    const isAlive = remainingDays < 1;
    const remainingView = isAlive ? (
        <Timer
            durationF={() => calculateTimer().remainingSeconds}
            handleEnd={() => {
                setVisible(false);
                clearDiscountInOtflow();
                closeDiscountInOutflowSaveModal();
            }}
        />
    ) : (
        `${remainingDays} ${daysPluralNominative(remainingDays)}`
    );

    const { handleCancel, handleClick } = useCallbacks({ setVisible, subscription, remainingDays });

    return (
        <div className={classNames(styles.root, { [styles.dark]: isDarkTheme })} onClick={handleClick}>
            <div className={styles.remainingTime}>
                <Icon20AlarmOutline width={18} height={18} />
                <span className={classNames({ [styles.fixedWidth]: isAlive })}>{remainingView}</span>
            </div>
            −{discountInOutflowValue}% на {subscription?.space.value}
            <IconButton
                className={styles.cross}
                onClick={(e) => {
                    discountInOutflowLogger.info('block close', expirationDate);
                    e.stopPropagation();
                    handleCancel();
                }}
            >
                <Icon20Cancel />
            </IconButton>
        </div>
    );
});

DiscountTimer.displayName = 'DiscountTimer';
